import { useEffect, useRef } from 'react'
import mapboxgl from 'mapbox-gl'

export interface UseMapClickHandlersProps {
  map: React.MutableRefObject<mapboxgl.Map | null>
  isLoaded: boolean
  styleLoaded: boolean
  onRouteClick?: (route: any) => void
  onWaypointClick?: (waypoint: any) => void
  onPOIClick?: (poi: any) => void
  onMapClick?: (coords: [number, number]) => void
}

/**
 * Hook for managing map click handlers
 * Handles clicks on routes, waypoints, POIs, and empty map areas
 */
export function useMapClickHandlers({
  map,
  isLoaded,
  styleLoaded,
  onRouteClick,
  onWaypointClick,
  onPOIClick,
  onMapClick
}: UseMapClickHandlersProps) {
  // Store callbacks in refs so handlers don't need to be re-attached
  const onRouteClickRef = useRef(onRouteClick)
  const onWaypointClickRef = useRef(onWaypointClick)
  const onPOIClickRef = useRef(onPOIClick)
  const onMapClickRef = useRef(onMapClick)

  // Store handlers in refs so we can remove them
  const routeClickHandlerRef = useRef<((e: mapboxgl.MapMouseEvent) => void) | null>(null)
  const poiClickHandlerRef = useRef<((e: mapboxgl.MapMouseEvent) => void) | null>(null)
  const mapClickHandlerRef = useRef<((e: mapboxgl.MapMouseEvent) => void) | null>(null)
  const waypointHandlersRef = useRef<{
    mouseenter?: () => void
    mouseleave?: () => void
    click?: (e: any) => void
  }>({})

  // Update refs when callbacks change
  useEffect(() => {
    onRouteClickRef.current = onRouteClick
    onWaypointClickRef.current = onWaypointClick
    onPOIClickRef.current = onPOIClick
    onMapClickRef.current = onMapClick
  }, [onRouteClick, onWaypointClick, onPOIClick, onMapClick])

  // Add click handlers after map and layers are ready
  useEffect(() => {
    if (!map.current || !isLoaded || !styleLoaded) return

    const addClickHandlers = () => {
      if (!map.current?.isStyleLoaded()) {
        map.current?.once('styledata', addClickHandlers)
        return
      }

      // Add route click handler
      if (onRouteClickRef.current && map.current.getLayer('route-lines')) {
        if (routeClickHandlerRef.current) {
          map.current.off('click', 'route-lines', routeClickHandlerRef.current)
        }
        
        routeClickHandlerRef.current = (e: mapboxgl.MapMouseEvent) => {
          try {
            const feature = e.features?.[0]
            if (!feature || !onRouteClickRef.current) return

            const rawProperties = feature.properties || {}
            const routeProperties = { ...rawProperties } as Record<string, any>

            // Convert common numeric/boolean fields from strings if necessary
            const numericFields = ['distance', 'duration', 'elevation_gain', 'elevation_loss', 'line_width', 'waypoint_count']
            numericFields.forEach(field => {
              if (typeof routeProperties[field] === 'string' && routeProperties[field].trim() !== '') {
                const parsed = Number(routeProperties[field])
                if (!Number.isNaN(parsed)) {
                  routeProperties[field] = parsed
                }
              }
            })

            if (typeof routeProperties.is_public === 'string') {
              routeProperties.is_public = routeProperties.is_public === 'true'
            }

            const route = {
              ...routeProperties,
              geometry: feature.geometry
            }

            onRouteClickRef.current(route)
          } catch (error) {
            if (map.current?.isStyleLoaded()) {
            }
          }
        }
        map.current.on('click', 'route-lines', routeClickHandlerRef.current)
      }

      // Add POI click handler
      if (onPOIClickRef.current && map.current.getLayer('poi-markers')) {
        if (poiClickHandlerRef.current) {
          map.current.off('click', 'poi-markers', poiClickHandlerRef.current)
        }
        
        poiClickHandlerRef.current = (e: mapboxgl.MapMouseEvent) => {
          try {
            const poi = e.features?.[0]?.properties
            if (poi && onPOIClickRef.current) {
              onPOIClickRef.current(poi)
            }
          } catch (error) {
            if (map.current?.isStyleLoaded()) {
            }
          }
        }
        map.current.on('click', 'poi-markers', poiClickHandlerRef.current)
      }

      // Add waypoint click handler (waypoints are handled separately in useMapLayers)
      // This is kept here for reference but waypoint clicks are handled in the waypoint layer effect

      // Add map click handler - ALWAYS add this
      if (onMapClickRef.current) {
        if (mapClickHandlerRef.current) {
          map.current.off('click', mapClickHandlerRef.current)
        }
        
        mapClickHandlerRef.current = (e: mapboxgl.MapMouseEvent) => {
          // Simple check: if e.features has interactable items, skip
          // Otherwise, always trigger map click
          if (e.features && e.features.length > 0) {
            const hasInteractable = e.features.some((feature: any) => {
              const layerId = feature.layer?.id
              const sourceId = feature.source
              return (
                layerId === 'route-lines' ||
                layerId === 'waypoint-markers' ||
                layerId === 'poi-markers' ||
                layerId === 'search-result-marker' ||
                layerId === 'selected-waypoint-marker' ||
                layerId === 'dead-end-markers' ||
                sourceId === 'waypoint-markers' ||
                sourceId === 'poi-markers' ||
                sourceId === 'search-result-marker' ||
                sourceId === 'dead-end-markers'
              )
            })
            
            if (hasInteractable) {
              // Clicked on an interactable feature, don't trigger map click
              return
            }
          }
          
          // Additional safety check: query rendered features (only if layers exist)
          if (map.current) {
            try {
              const existingLayers: string[] = []
              const layersToCheck = ['waypoint-markers', 'route-lines', 'poi-markers', 'search-result-marker', 'selected-waypoint-marker', 'dead-end-markers']
              
              for (const layerId of layersToCheck) {
                if (map.current.getLayer(layerId)) {
                  existingLayers.push(layerId)
                }
              }
              
              if (existingLayers.length > 0) {
                const featuresAtPoint = map.current.queryRenderedFeatures(e.point, {
                  layers: existingLayers
                })
                
                if (featuresAtPoint && featuresAtPoint.length > 0) {
                  // Clicked on an interactable feature
                  return
                }
              }
            } catch (error) {
              // If query fails, continue - assume it's a valid map click
            }
          }
          
          // Trigger the map click callback
          if (onMapClickRef.current) {
            onMapClickRef.current([e.lngLat.lng, e.lngLat.lat])
          }
        }
        
        if (map.current) {
          map.current.on('click', mapClickHandlerRef.current)
        }
      } else {
      }
    }

    // Add handlers when map is ready
    if (isLoaded && styleLoaded && map.current?.isStyleLoaded()) {
      addClickHandlers()
    } else {
      const timeout = setTimeout(() => {
        addClickHandlers()
      }, 100)

      return () => {
        clearTimeout(timeout)
      }
    }
  }, [isLoaded, styleLoaded, map])

  // Return waypoint handlers ref for use in layer management
  return {
    waypointHandlersRef
  }
}

